import { join } from "node:path";

import { resolveHomeDirectory } from "./home.js";
import type {
  ManagedAgent,
  ManagedAgentId,
  ManagedEntrypointKind,
  ManagedPromptKind,
  SetupScope,
} from "./types.js";

const AGENT_LABELS: Record<string, string> = {
  adal: "AdaL",
  amp: "Amp",
  antigravity: "Antigravity",
  augment: "Augment",
  bob: "IBM Bob",
  "claude-code": "Claude Code",
  cline: "Cline",
  codebuddy: "CodeBuddy",
  "command-code": "Command Code",
  continue: "Continue",
  codex: "Codex",
  cortex: "Cortex Code",
  crush: "Crush",
  cursor: "Cursor",
  deepagents: "Deep Agents",
  droid: "Droid",
  firebender: "Firebender",
  "gemini-cli": "Gemini CLI",
  "github-copilot": "GitHub Copilot",
  goose: "Goose",
  "iflow-cli": "iFlow CLI",
  junie: "Junie",
  kilo: "Kilo Code",
  "kimi-cli": "Kimi Code CLI",
  "kiro-cli": "Kiro CLI",
  kode: "Kode",
  mcpjam: "MCPJam",
  "mistral-vibe": "Mistral Vibe",
  mux: "Mux",
  neovate: "Neovate",
  opencode: "OpenCode",
  openclaw: "OpenClaw",
  openhands: "OpenHands",
  pi: "Pi",
  pochi: "Pochi",
  qoder: "Qoder",
  "qwen-code": "Qwen Code",
  replit: "Replit",
  roo: "Roo Code",
  "trae-cn": "Trae CN",
  trae: "Trae",
  universal: "Universal",
  warp: "Warp",
  windsurf: "Windsurf",
  zencoder: "Zencoder",
};

type PathResolver = (projectRoot: string, home: string) => string;

type TargetConfig<TKind extends ManagedEntrypointKind | ManagedPromptKind> = {
  global?: PathResolver;
  kind: TKind;
  project?: PathResolver;
};

type SupportedAgentConfig = {
  command?: TargetConfig<ManagedEntrypointKind>;
  prompt?: TargetConfig<ManagedPromptKind>;
};

const SUPPORT_MATRIX: Record<ManagedAgentId, SupportedAgentConfig> = {
  amp: {
    prompt: {
      global: (_projectRoot, home) => join(home, ".config", "amp", "AGENTS.md"),
      kind: "append-block",
      project: (projectRoot) => join(projectRoot, "AGENTS.md"),
    },
  },
  "claude-code": {
    command: {
      global: (_projectRoot, home) =>
        join(home, ".claude", "commands", "emergent-thinking.md"),
      kind: "claude-command",
      project: (projectRoot) =>
        join(projectRoot, ".claude", "commands", "emergent-thinking.md"),
    },
    prompt: {
      global: (_projectRoot, home) => join(home, ".claude", "CLAUDE.md"),
      kind: "append-block",
      project: (projectRoot) => join(projectRoot, "CLAUDE.md"),
    },
  },
  cline: {
    command: {
      global: (_projectRoot, home) =>
        join(home, "Documents", "Cline", "Workflows", "emergent-thinking.md"),
      kind: "cline-workflow",
      project: (projectRoot) =>
        join(projectRoot, ".clinerules", "workflows", "emergent-thinking.md"),
    },
    prompt: {
      global: (_projectRoot, home) =>
        join(home, "Documents", "Cline", "Rules", "emergent-thinking.md"),
      kind: "cline-rule",
      project: (projectRoot) => join(projectRoot, ".clinerules", "emergent-thinking.md"),
    },
  },
  codex: {
    command: {
      global: (_projectRoot, home) =>
        join(home, ".codex", "prompts", "emergent-thinking.md"),
      kind: "claude-command",
      project: (projectRoot) =>
        join(projectRoot, ".codex", "prompts", "emergent-thinking.md"),
    },
    prompt: {
      global: (_projectRoot, home) => join(home, ".codex", "AGENTS.md"),
      kind: "append-block",
      project: (projectRoot) => join(projectRoot, "AGENTS.md"),
    },
  },
  cursor: {
    command: {
      kind: "cursor-command",
      project: (projectRoot) =>
        join(projectRoot, ".cursor", "commands", "emergent-thinking.md"),
    },
    prompt: {
      kind: "cursor-rule",
      project: (projectRoot) =>
        join(projectRoot, ".cursor", "rules", "emergent-thinking.mdc"),
    },
  },
  firebender: {
    prompt: {
      global: (_projectRoot, home) =>
        join(home, ".firebender", "rules", "emergent-thinking.mdc"),
      kind: "firebender-rule",
      project: (projectRoot) =>
        join(projectRoot, ".firebender", "rules", "emergent-thinking.mdc"),
    },
  },
  "gemini-cli": {
    command: {
      global: (_projectRoot, home) =>
        join(home, ".gemini", "commands", "emergent-thinking.toml"),
      kind: "gemini-command",
      project: (projectRoot) =>
        join(projectRoot, ".gemini", "commands", "emergent-thinking.toml"),
    },
    prompt: {
      global: (_projectRoot, home) => join(home, ".gemini", "GEMINI.md"),
      kind: "append-block",
      project: (projectRoot) => join(projectRoot, "GEMINI.md"),
    },
  },
  "github-copilot": {
    command: {
      kind: "copilot-prompt",
      project: (projectRoot) =>
        join(projectRoot, ".github", "prompts", "emergent-thinking.prompt.md"),
    },
    prompt: {
      kind: "append-block",
      project: (projectRoot) => join(projectRoot, ".github", "copilot-instructions.md"),
    },
  },
  "kimi-cli": {
    prompt: {
      kind: "append-block",
      project: (projectRoot) => join(projectRoot, "AGENTS.md"),
    },
  },
  opencode: {
    command: {
      global: (_projectRoot, home) =>
        join(home, ".config", "opencode", "commands", "emergent-thinking.md"),
      kind: "opencode-command",
      project: (projectRoot) =>
        join(projectRoot, ".opencode", "commands", "emergent-thinking.md"),
    },
    prompt: {
      kind: "append-block",
      project: (projectRoot) => join(projectRoot, "AGENTS.md"),
    },
  },
  qoder: {
    command: {
      global: (_projectRoot, home) =>
        join(home, ".qoder", "commands", "emergent-thinking.md"),
      kind: "qoder-command",
      project: (projectRoot) =>
        join(projectRoot, ".qoder", "commands", "emergent-thinking.md"),
    },
    prompt: {
      global: (_projectRoot, home) => join(home, ".qoder", "AGENTS.md"),
      kind: "append-block",
      project: (projectRoot) => join(projectRoot, "AGENTS.md"),
    },
  },
  warp: {
    prompt: {
      kind: "append-block",
      project: (projectRoot) => join(projectRoot, "AGENTS.md"),
    },
  },
};

export function formatAgentLabel(agentId: string) {
  return AGENT_LABELS[agentId] ?? agentId;
}

export function resolveManagedAgents(
  agentIds: readonly string[],
  scope: SetupScope,
  projectRoot: string,
  env: NodeJS.ProcessEnv = process.env,
) {
  const resolvedHome = resolveHomeDirectory(env);
  const managedAgents: ManagedAgent[] = [];

  for (const agentId of agentIds) {
    if (!isManagedAgentId(agentId)) {
      continue;
    }

    const config = SUPPORT_MATRIX[agentId];
    const commandResolver = scope === "global" ? config.command?.global : config.command?.project;
    const promptResolver = scope === "global" ? config.prompt?.global : config.prompt?.project;

    const commandTarget =
      commandResolver === undefined
        ? undefined
        : {
            kind: config.command?.kind ?? "markdown-command",
            path: commandResolver(projectRoot, resolvedHome),
          };
    const promptTarget =
      promptResolver === undefined
        ? undefined
        : {
            kind: config.prompt?.kind ?? "append-block",
            path: promptResolver(projectRoot, resolvedHome),
          };

    const nextAgent: ManagedAgent = {
      id: agentId,
      label: formatAgentLabel(agentId),
    };
    if (commandTarget !== undefined) {
      nextAgent.commandTarget = commandTarget;
    }
    if (promptTarget !== undefined) {
      nextAgent.promptTarget = promptTarget;
    }

    managedAgents.push(nextAgent);
  }

  return managedAgents;
}

export function isManagedAgentId(agentId: string): agentId is ManagedAgentId {
  return Object.prototype.hasOwnProperty.call(SUPPORT_MATRIX, agentId);
}
